import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Filter, X } from 'lucide-react'
import { useNavigate } from 'react-router'
import type { Slot } from '../lib/types'
import { groupByDay, mytHour, slotState, slotWindow } from '../lib/slot-time'
import { useSlots, useTick } from '../features/useSlots'
import { NowLine, SlotRow } from '../features/SlotRow'
import { SlotDetail } from '../features/SlotDetail'
import { Button } from '../ui/Button'
import { Notice, Skeleton } from '../ui/Feedback'
import { Sheet } from '../ui/Sheet'
import { useAuthed } from '../lib/session'
import { getMode } from '../lib/api'
import { EVENT, mytDateKey } from '../config/event'

export function ProgrammeRoute() {
  const { slots, loading, error, refresh } = useSlots()
  const now = useTick()
  const navigate = useNavigate()
  const session = useAuthed()
  const [detail, setDetail] = useState<Slot | null>(null)
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  // Demo starts in All so the full conference programme is visible immediately.
  const [room, setRoom] = useState('all')
  const [track, setTrack] = useState('all')
  const [filterOpen, setFilterOpen] = useState(false)

  /*
   * Switching day scrolls back to that day's first session. Without it the new day
   * opened wherever the old one had been scrolled to - tap Day 3 from the bottom of
   * today and you land on its closing ceremony, not its 07:00 breakfast. Only fires
   * for a tap: the first render and the store's refetches leave the scroll alone.
   */
  const daybarRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)
  const dayTapped = useRef(false)

  const days = useMemo(() => groupByDay(slots), [slots])

  useLayoutEffect(() => {
    if (!dayTapped.current) return
    dayTapped.current = false
    const bar = daybarRef.current
    const list = timelineRef.current
    if (!bar || !list) return
    if (list.getBoundingClientRect().top >= bar.getBoundingClientRect().bottom) return // Already on screen.
    /*
     * scrollIntoView, not scrollBy: a shorter day shrinks the document, and a delta
     * measured against the old scroll position overshot - Day 3 tapped from the foot
     * of today landed on 09:30 instead of the first row. Let the browser clamp. The
     * bar is sticky, so its height is the margin the first row needs to clear it.
     */
    list.style.scrollMarginTop = `${bar.offsetHeight}px`
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    list.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' })
  }, [selectedDay])
  const apiMode = getMode()
  // Derived from EVENT rather than hardcoded, so the demo-day guard cannot drift out
  // of step with config/event.ts and mock/fixtures.ts the way it did before.
  const expectedDemoDays = useMemo(() => {
    const out: string[] = []
    const start = new Date(`${EVENT.startDate}T00:00:00Z`)
    const end = new Date(`${EVENT.endDate}T00:00:00Z`)
    for (let d = start; d <= end; d = new Date(d.getTime() + 86_400_000)) {
      out.push(d.toISOString().slice(0, 10))
    }
    return out
  }, [])
  const missingDemoDays = apiMode === 'mock' ? expectedDemoDays.filter((date) => !days.some((day) => day.key === date)) : []

  /*
   * Today, in Malaysian local time, in the same shape `groupByDay` keys on.
   *
   * `groupByDay` takes `slot_begin.slice(0, 10)` - the raw digits the driver returns,
   * which are MYT wall-clock whatever the trailing `Z` claims - and `mytDateKey` formats
   * through `en-CA`, which is also `YYYY-MM-DD`. So these are directly comparable, and
   * anything that compares them has to keep both halves in MYT or it will be a day out
   * for anyone west of Kuala Lumpur in the evening.
   */
  const todayKey = mytDateKey(now)
  const todayIndex = days.findIndex((day) => day.key === todayKey)

  /*
   * Open on today when the event is running, and on the first day when it is not.
   *
   * This used to open on `days[0]` unconditionally, so on the second morning of a
   * three-day course everyone landed on day one and had to work out that they were
   * looking at yesterday.
   */
  const activeDay = selectedDay && days.some((day) => day.key === selectedDay)
    ? selectedDay
    : days[todayIndex]?.key ?? days[0]?.key ?? null

  const rooms = useMemo(
    () => [...new Set(slots.map((slot) => slot.venue_name).filter(Boolean))].sort(),
    [slots],
  )

  const tracks = useMemo(() => {
    const values = slots.map((slot) => {
      const match = slot.slot_name.match(/(?:cardiology|infectious disease|technology|ai)/i)
      return match?.[0] ? match[0].replace(/\b\w/g, (c) => c.toUpperCase()) : null
    }).filter(Boolean) as string[]
    return [...new Set(values)].sort()
  }, [slots])

  const filteredSlots = useMemo(() => {
    /*
     * No "My schedule" filter any more. /getSlot already returns only this delegate's
     * track, so the list was never anyone else's schedule; the tab actually filtered to
     * qr_required, hiding every meal and break - including the last day's dispersal.
     * Each row now says whether it needs a check-in, which is where that belongs.
     */
    let next = slots
    if (room !== 'all') next = next.filter((slot) => slot.venue_name === room)
    if (track !== 'all') next = next.filter((slot) => slot.slot_name.toLowerCase().includes(track.toLowerCase()))
    return next
  }, [slots, room, track])

  const activeDayData = useMemo(
    () => groupByDay(filteredSlots).find((day) => day.key === activeDay),
    [filteredSlots, activeDay],
  )


  function startCheckIn(slot: Slot) {
    setDetail(null)
    void navigate(`/scan/${slot.id}`)
  }

  return (
    <main className="page programme-page">
      <header className="programme-masthead" aria-label="Your schedule">
        <div className="programme-masthead__copy">
          <p className="kicker">Your schedule</p>
          <h1 className="display programme-page__title">Programme</h1>
          {/*
            * The track, and not a greeting.
            *
            * This line read "Good morning, Dr Amirah Binti Kamarudin - Pegawai
            * Perubatan/Penolong Pegawai Perubatan" at 9.9px over two lines. Home already
            * greets by name, so three quarters of it was a second hello in the smallest
            * type on the screen, above the one thing this screen exists to show.
            *
            * The track stays because it is the only thing that answers "whose programme
            * is this?" - /getSlot returns one delegate's track, not the whole event, and
            * nothing else on the page says so.
            */}
          <p className="programme-personal-sub">{session.track_name || 'Your personal schedule'}</p>
        </div>
      </header>

      {apiMode === 'mock' && missingDemoDays.length > 0 && slots.length > 0 ? (
        <div className="programme-data-warning" role="status">
          Demo data returned {days.length} programme day{days.length === 1 ? '' : 's'}; expected 3. Missing: {missingDemoDays.map((date) => new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', timeZone: 'Asia/Kuala_Lumpur' }).format(new Date(`${date}T00:00:00Z`))).join(', ')}.
        </div>
      ) : null}

      {loading && slots.length === 0 ? <ProgrammeSkeleton /> : null}

      {!loading && slots.length === 0 ? (
        <Notice
          title={error ? 'Could not load the programme' : 'Nothing scheduled yet'}
          body={error ? 'Check your connection and try again.' : 'Once sessions are assigned to your track they will appear here.'}
          action={{ label: 'Try again', onClick: refresh }}
        />
      ) : null}

      {days.length > 0 ? (
        <>
          {/*
            * Day picker and date rule stick together as one block. Sticking them as two
            * siblings would need the second to know the first's exact height for its
            * `top`, which changes with the user's text size; one wrapper cannot get that
            * wrong.
            */}
          <div className="programme-daybar" ref={daybarRef}>
          <div className="programme-day-strip" data-day-count={days.length} role="tablist" aria-label="Event days">
            {days.map((day, index) => {
              const date = parseDay(day.label, day.key)
              const active = day.key === activeDay
              /*
               * Compared against the real date, not against the position in the list.
               * This was `index === 0`, which labelled the first day "Today" forever -
               * so on 27 August, two days after the course ended, the picker still
               * announced 24 August as today. It no longer replaces the day number
               * (that left the strip reading Day 1 / Today / Day 3); it marks the card
               * for assistive tech and the highlight does the rest.
               */
              const today = day.key === todayKey
              const count = day.slots.length
              return (
                <button
                  key={day.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className="programme-day-card"
                  data-active={active || undefined}
                  data-today={today || undefined}
                  aria-current={today ? 'date' : undefined}
                  onClick={() => { dayTapped.current = true; setSelectedDay(day.key) }}
                >
                  <span>Day {index + 1}</span>
                  <strong>{date.day} {date.month}</strong>
                  <em>{date.weekday}</em>
                  <small>{count} sessions</small>
                </button>
              )
            })}
          </div>

          {SHOW_FILTER ? (
            <div className="programme-viewbar programme-viewbar--filter-only">
              <button className="programme-filter-button" type="button" onClick={() => setFilterOpen(true)}>
                <Filter size={14} /> Filter
                {(room !== 'all' || track !== 'all') ? <span className="programme-filter-dot" /> : null}
              </button>
            </div>
          ) : null}

          {/*
            * No context bar.
            *
            * It printed "Tue - 25 Aug" and "10 sessions" 8px beneath a day card already
            * reading Today / 25 Aug / Tue / 10 sessions in solid cobalt - the same two
            * facts, restated in smaller type, under the heaviest rule on the page
            * (`border-top: 1px solid var(--ink)`, drawn beneath a repetition).
            *
            * One caveat for whoever turns the filters back on: this bar was the only
            * place a *filtered* count could appear, and the day card's count is always
            * unfiltered. With SHOW_FILTER false, `filteredSlots` is a pass-through and
            * the two can never disagree - which is exactly why it is safe to delete
            * today and would need rethinking on the day that changes.
            */}
          </div>

          {activeDayData ? (
            <div ref={timelineRef}>
            <Timeline
              slots={activeDayData.slots}
              now={now}
              /* The rail marks where you are, so it belongs on the day you are in. */
              showNow={activeDay === todayKey}
              onOpen={setDetail}
              onCheckIn={startCheckIn}
            />
            </div>
          ) : (
            <div className="programme-empty">No sessions match these filters.</div>
          )}
        </>
      ) : null}

      <Sheet open={SHOW_FILTER && filterOpen} onClose={() => setFilterOpen(false)} title="Programme filters">
        <div className="programme-sheet-head">
          <div>
            <p className="kicker">Refine programme</p>
            <h2 className="display">Filter</h2>
          </div>
          <button className="programme-sheet-close" onClick={() => setFilterOpen(false)} aria-label="Close filter"><X size={18} /></button>
        </div>
        <div className="programme-filter-group">
          <p className="kicker">Room</p>
          <label><input type="radio" name="room" checked={room === 'all'} onChange={() => setRoom('all')} /> All rooms</label>
          {rooms.map((name) => <label key={name}><input type="radio" name="room" checked={room === name} onChange={() => setRoom(name)} /> {name}</label>)}
        </div>
        {tracks.length > 0 ? (
          <div className="programme-filter-group">
            <p className="kicker">Track</p>
            <label><input type="radio" name="track" checked={track === 'all'} onChange={() => setTrack('all')} /> All tracks</label>
            {tracks.map((name) => <label key={name}><input type="radio" name="track" checked={track === name} onChange={() => setTrack(name)} /> {name}</label>)}
          </div>
        ) : null}
        <Button variant="primary" onClick={() => setFilterOpen(false)}>Apply filter</Button>
        <button className="programme-clear" onClick={() => { setRoom('all'); setTrack('all') }}>Clear filters</button>
      </Sheet>

      <SlotDetail slot={detail} now={now} onClose={() => setDetail(null)} onCheckIn={startCheckIn} />
    </main>
  )
}

/*
 * Hidden, not removed - flip to true to bring it back.
 *
 * The room and track filters are switched off while we see whether they earn a
 * permanent control. Both are narrower than they look: /getSlot already returns only
 * this delegate's track, so the "track" filter is matching a substring of the session
 * title rather than an actual track, and one delegate's programme rarely spans enough
 * rooms for room filtering to beat scrolling.
 *
 * The state and the sheet are deliberately left wired up. `room` and `track` stay at
 * 'all', so filteredSlots is a pass-through and nothing downstream changes.
 */
const SHOW_FILTER = false

const PERIODS = ['Morning', 'Afternoon', 'Evening'] as const
type Period = (typeof PERIODS)[number]

/*
 * These headings were computed as `slotWindow(slot).start % 1440`. `start` is epoch
 * milliseconds, so that was milliseconds modulo 1.44 seconds rather than minutes of
 * day - an arbitrary number with no relation to the clock. In practice it came out 0
 * for every slot in the event, so all 21 filed under "Morning" and a 20:00 session sat
 * beneath it, while "Afternoon" and "Evening" were unreachable.
 *
 * The hour has to come from a Malaysia-pinned formatter for the same reason every
 * other time on this screen does: a delegate whose phone is still on another zone
 * should see the programme as printed.
 */
function periodOf(slot: Slot): Period {
  const hour = mytHour(slotWindow(slot).start)
  // A slot whose time will not parse still has to appear. The old three filters each
  // rejected NaN, so such a slot matched no group at all and dropped out of the
  // programme silently; here every slot lands in exactly one bucket.
  if (!Number.isFinite(hour) || hour < 12) return 'Morning'
  return hour < 18 ? 'Afternoon' : 'Evening'
}

function Timeline({ slots, now, showNow, onOpen, onCheckIn }: { slots: Slot[]; now: number; showNow: boolean; onOpen: (slot: Slot) => void; onCheckIn: (slot: Slot) => void }) {
  const groups = PERIODS.map((label) => [label, slots.filter((s) => periodOf(s) === label)] as const)

  /*
   * Where the rail's "now" marker goes, decided once over the whole day.
   *
   * It used to be decided per period, against that period's own slots - so every period
   * whose first session had not started yet found a "next" of its own and drew a marker.
   * At 10:16 that put NOW at the top of Morning, again at the top of Afternoon, and
   * again at the top of Evening. A position can only be in one place.
   *
   * The flat index is the one already being threaded through as `startOffset + i` for
   * the row animation, so the comparison costs nothing new.
   */
  const flat = groups.flatMap(([, group]) => group)
  const markAt = showNow ? nowMarkerIndex(flat, now) : -1

  let offset = 0
  return (
    <div className="programme-timeline">
      {groups.map(([label, group]) => {
        if (!group.length) return null
        const startOffset = offset
        offset += group.length
        return (
          <section key={label} className="programme-period">
            <h2>{label}</h2>
            <ul className="sched__list">
              {group.map((slot, i) => (
                <Fragment key={slot.id}>
                  {startOffset + i === markAt ? <NowLine now={now} /> : null}
                  <SlotRow slot={slot} now={now} index={startOffset + i} onOpen={onOpen} onCheckIn={onCheckIn} />
                </Fragment>
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

function parseDay(label: string, key: string) {
  const date = new Date(`${key}T00:00:00Z`)
  if (!Number.isNaN(date.getTime())) {
    const fmt = (options: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'Asia/Kuala_Lumpur' }).format(date)
    return { day: fmt({ day: '2-digit' }), month: fmt({ month: 'short' }), weekday: fmt({ weekday: 'short' }) }
  }
  const parts = label.split(' ')
  return { day: parts[0] ?? '--', month: parts[1] ?? '', weekday: parts[2] ?? '' }
}

/**
 * The row the "now" marker sits above, as a flat index across the whole day, or -1.
 *
 * Immediately before the session containing the current time, or before the next one
 * when between sessions. That makes the marker read as a position in the chronological
 * rail rather than as a fake slot. -1 when the day is entirely behind us, which is what
 * a day with nothing live and nothing still to start means.
 */
function nowMarkerIndex(slots: Slot[], now: number): number {
  const live = slots.findIndex((s) => slotState(s, now) === 'live')
  if (live >= 0) return live
  return slots.findIndex((s) => slotWindow(s).start > now)
}

function ProgrammeSkeleton() {
  return <div className="programme-skeleton">{[1, 2, 3, 4].map((i) => <div key={i}><Skeleton height=".7rem" width="3rem" /><Skeleton height="1.1rem" width={`${70 + i * 5}%`} /></div>)}</div>
}
